import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./popupsright.css";
import firecorrect from "../images/firecorrect.png";

const toLocalISODate = (dateValue) => {
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeDbDay = (value) => String(value || "").slice(0, 10);

const getMondayOfCurrentWeek = (dateValue) => {
  const date = new Date(dateValue);
  date.setHours(0, 0, 0, 0);
  const dayOffsetFromMonday = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - dayOffsetFromMonday);
  return date;
};

const FlameButtonWithDropdown = () => {
  const btnRef = useRef(null);
  const popupRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const streakTargetDays = 7;

  const streakDayLabels = ["L", "Ma", "Mi", "J", "V", "S", "D"];
  const currentDayIndex = (() => {
    const nativeDay = new Date().getDay();
    return nativeDay === 0 ? 6 : nativeDay - 1;
  })();

  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [openedDayKey, setOpenedDayKey] = useState(null);
  const [streakInfo, setStreakInfo] = useState({
    isLoading: false,
    currentStreak: 0,
    todayXp: 0,
    weekDays: [],
  });

  const fetchStreakData = async () => {
    setStreakInfo((previous) => ({ ...previous, isLoading: true }));

    try {
      const response = await fetch(`${apiUrl}/daily-lessons`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Nu s-au putut incarca datele de streak.");
      }

      const payload = await response.json();
      const xpByDay = new Map();

      const upsertDayXp = (row) => {
        const key = normalizeDbDay(row?.day);
        if (!key) return;
        xpByDay.set(key, Number(row?.daily_xp || 0));
      };

      upsertDayXp(payload?.today);
      (Array.isArray(payload?.history) ? payload.history : []).forEach(upsertDayXp);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayKey = toLocalISODate(today);

      let currentStreak = 0;
      const cursor = new Date(today);

      while (true) {
        const cursorKey = toLocalISODate(cursor);
        const xpForDay = Number(xpByDay.get(cursorKey) || 0);

        if (xpForDay > 0) {
          currentStreak += 1;
          cursor.setDate(cursor.getDate() - 1);
          continue;
        }

        break;
      }

      const streakStart = new Date(today);
      streakStart.setDate(today.getDate() - Math.max(currentStreak - 1, 0));
      streakStart.setHours(0, 0, 0, 0);

      const weekStart = getMondayOfCurrentWeek(today);
      const weekDays = streakDayLabels.map((label, index) => {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + index);
        date.setHours(0, 0, 0, 0);

        const dayKey = toLocalISODate(date);
        const xp = Number(xpByDay.get(dayKey) || 0);

        const isCurrentDay = dayKey === todayKey;
        const isInCurrentStreak =
          currentStreak > 0 &&
          xp > 0 &&
          date.getTime() >= streakStart.getTime() &&
          date.getTime() <= today.getTime();

        return {
          key: dayKey,
          label,
          xp,
          isCurrentDay,
          isInCurrentStreak,
        };
      });

      setStreakInfo({
        isLoading: false,
        currentStreak,
        todayXp: Number(xpByDay.get(todayKey) || 0),
        weekDays,
      });
    } catch (error) {
      setStreakInfo((previous) => ({
        ...previous,
        isLoading: false,
      }));
    }
  };

  const openPopup = () => {
    if (isVisible && isOpen) return;
    setIsVisible(true);
    requestAnimationFrame(() => setIsOpen(true));
  };

  const closePopup = () => {
    if (!isVisible) return;
    setIsOpen(false);
  };

  // detect mobile / desktop
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    fetchStreakData();
  }, []);

  useEffect(() => {
    if (isVisible) {
      fetchStreakData();
      setOpenedDayKey(null);
    }
  }, [isVisible]);

  // inchide popup la click in afara
  useEffect(() => {
    const close = (e) => {
      if (
        isVisible &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        !btnRef.current?.contains(e.target)
      ) {
        closePopup();
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const closeTimer = setTimeout(() => {
      if (!isOpen) {
        setIsVisible(false);
      }
    }, 280);

    return () => clearTimeout(closeTimer);
  }, [isVisible, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("stats-popup-open");
    } else {
      document.body.classList.remove("stats-popup-open");
    }

    return () => {
      document.body.classList.remove("stats-popup-open");
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isVisible) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closePopup();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isVisible]);

  const streakProgressPercent = Math.min(
    100,
    Math.round((streakInfo.currentStreak / streakTargetDays) * 100)
  );

  const dayItems = streakInfo.weekDays.length
    ? streakInfo.weekDays
    : streakDayLabels.map((label, index) => ({
        key: `fallback-${label}-${index}`,
        label,
        xp: 0,
        isCurrentDay: index === currentDayIndex,
        isInCurrentStreak: false,
      }));

  const streakTitle = `${streakInfo.currentStreak} ${
    streakInfo.currentStreak === 1 ? "zi consecutiva" : "zile consecutive"
  }`;

  const streakSubtitle =
    streakInfo.todayXp > 0
      ? `Ai ${streakInfo.todayXp} XP azi. Continua seria!`
      : "Finalizeaza o lectie azi pentru a incepe un nou streak!";

  const remainingDays = Math.max(streakTargetDays - streakInfo.currentStreak, 0);
  const clubDescription =
    remainingDays === 0
      ? "Seria este activa. Continua zilnic ca sa o mentii!"
      : `Finalizeaza inca ${remainingDays} ${remainingDays === 1 ? "zi" : "zile"} consecutive de studiu pentru a mentine seria.`;

  return (
    <div className="first-main-pagi-flame-wrapper">
      {/* BUTON */}
      <div
        ref={btnRef}
        className="first-main-pagi-stats-button"
        onClick={isOpen ? closePopup : openPopup}
      >
        <img
          src={firecorrect}
          alt="flame"
          className="first-main-pagi-stats-icon"
        />
        <div className="first-main-pagi-stats-number">{streakInfo.currentStreak}</div>
      </div>

      {isVisible && createPortal(
        <>
          <div
            className={`first-main-pagi-overlay first-main-pagi-flame-overlay ${isOpen ? "open" : ""}`}
            onClick={closePopup}
          />

          <div
            ref={popupRef}
            className={`first-main-pagi-popup first-main-pagi-flame-popup first-main-pagi-flame-popup-anim ${isOpen ? "open" : ""} ${
              isMobile ? "mobile" : "desktop"
            }`}
          >
            <div className="streak-popup-header">
              <div className="streak-title">{streakTitle}</div>
              <div className="streak-subtitle">{streakInfo.isLoading ? "Se incarca datele de streak..." : streakSubtitle}</div>
              <div className="streak-flame-icon">
                <span role="img" aria-label="flame" style={{fontSize: '2rem'}}><img
          src={firecorrect}
          alt="flame"
          className="first-main-pagi-stats-icon"
        /></span>
              </div>
            </div>

            <div className="streak-days-row">
              {dayItems.map((dayItem) => (
                <div className="streak-day-wrap" key={dayItem.key}>
                  <button
                    type="button"
                    className={`streak-day ${dayItem.isCurrentDay ? "active" : ""} ${dayItem.isInCurrentStreak ? "completed" : ""}`.trim()}
                    onClick={() => {
                      setOpenedDayKey((previousKey) =>
                        previousKey === dayItem.key ? null : dayItem.key
                      );
                    }}
                    aria-label={`XP pentru ${dayItem.label}`}
                    aria-expanded={openedDayKey === dayItem.key}
                  >
                    {dayItem.label}
                  </button>

                  {openedDayKey === dayItem.key && (
                    <div className="streak-day-xp-popover" role="status" aria-live="polite">
                      {Number(dayItem.xp || 0)} XP
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="streak-club-box">
              <div className="streak-club-lock">
                <span role="img" aria-label="lock" style={{fontSize: '1.5rem', marginRight: 8}}></span>
                <span className="streak-club-title">Mentine seria</span>
              </div>
              <div className="streak-club-desc">{clubDescription}</div>
              <div className="streak-progress-bar">
                <div className="streak-progress" style={{width: `${streakProgressPercent}%`}}></div>
                <span className="streak-progress-flame" style={{left: `clamp(12px, ${streakProgressPercent}%, calc(100% - 12px))`}}>
                  <span role="img" aria-label="flame"><img
          src={firecorrect}
          alt="flame"
          className="first-main-pagi-stats-icon"
        /></span>
                </span>
              </div>
            </div>

            <button className="streak-view-more-btn"><span className="color-button-streak">Vezi progresul</span></button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default FlameButtonWithDropdown;
